import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, Award } from 'lucide-react';
import apiClient from '../../utils/api';
import AppHeader from '../Header/AppHeader';
import './CreditHistory.css';

function CreditHistory() {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    loadHistory();
    loadSubscriptionStatus();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/credits/history?limit=100`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const data = await apiClient.getSubscriptionStatus();
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'addition':
      case 'purchase':
      case 'subscription_grant':
      case 'admin_grant':
        return <TrendingUp className="transaction-icon positive" />;
      case 'deduction':
      case 'chat_message':
      case 'admin_deduct':
        return <TrendingDown className="transaction-icon negative" />;
      case 'daily_bonus':
        return <Award className="transaction-icon bonus" />;
      default:
        return <Calendar className="transaction-icon neutral" />;
    }
  };

  const getTransactionColor = (type) => {
    if (['addition', 'purchase', 'subscription_grant', 'admin_grant', 'daily_bonus'].includes(type)) {
      return 'positive';
    }
    return 'negative';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeLabel = (type) => {
    const labels = {
      'purchase': 'Coin-Kauf',
      'subscription_grant': 'Abo-Credits',
      'admin_grant': 'Admin-Gutschrift',
      'admin_deduct': 'Admin-Abzug',
      'chat_message': 'Chat-Nachricht',
      'daily_bonus': 'Tagesbonus',
      'addition': 'Gutschrift',
      'deduction': 'Abzug'
    };
    return labels[type] || type;
  };

  const getCreditTypeLabel = (type) => {
    const labels = {
      'daily': 'Täglich',
      'monthly': 'Monatlich',
      'permanent': 'Permanent'
    };
    return labels[type] || type;
  };

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="credit-history-page" data-testid="credit-history-page">
        <div className="credit-history-container">
          <div className="page-header">
            <button onClick={() => navigate('/dashboard')} className="btn-back">
              <ArrowLeft size={20} />
              Zurück
            </button>
            <h1 className="page-title">💰 Coin-Verlauf</h1>
            <div></div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Lade Transaktionen...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h2>Keine Transaktionen</h2>
              <p>Du hast noch keine Coin-Transaktionen.</p>
            </div>
          ) : (
            <div className="transactions-list">
              {transactions.map((tx) => (
                <div 
                  key={tx.id} 
                  className={`transaction-card ${getTransactionColor(tx.transaction_type)}`}
                  data-testid={`transaction-${tx.id}`}
                >
                  <div className="transaction-main">
                    <div className="transaction-icon-wrapper">
                      {getTransactionIcon(tx.transaction_type)}
                    </div>
                    
                    <div className="transaction-details">
                      <div className="transaction-type">
                        {getTypeLabel(tx.transaction_type)}
                      </div>
                      <div className="transaction-reason">
                        {tx.reason || tx.description || 'Keine Beschreibung'}
                      </div>
                      <div className="transaction-meta">
                        <span className="credit-type-badge">
                          {getCreditTypeLabel(tx.credit_type)}
                        </span>
                        <span className="transaction-date">
                          <Calendar size={14} />
                          {formatDate(tx.created_at)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="transaction-amount">
                      <span className={`amount ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                        {tx.amount > 0 ? '+' : ''}{tx.amount}
                      </span>
                      <span className="amount-label">Coins</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default CreditHistory;
